import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * The two rights the published privacy policy promises: see what we hold, and
 * have it deleted. Until these existed both meant emailing a human who would
 * run SQL — a promise that depended on someone being available and remembering.
 *
 * Spec: docs/superpowers/specs/2026-09-12-privacy-compliance-design.md §15.
 */

export type ExportResult = { ok: true; data: unknown } | { ok: false; error: string };

/**
 * Everything we hold about the signed-in player, in one JSON document.
 *
 * Read with the player's OWN token, so row-level security decides what comes
 * back — this can never return somebody else's data even if it is asked to.
 */
export async function exportMyData(client: SupabaseClient): Promise<ExportResult> {
  const { data: userData } = await client.auth.getUser();
  const user = userData.user;
  if (!user) return { ok: false, error: 'Not signed in' };

  const [profile, save, inbox, codes, extras] = await Promise.all([
    // Explicit columns, not '*': recovery_hash is deliberately outside the
    // client's SELECT grant, and a wildcard would ask for it and be refused.
    client
      .from('profiles')
      .select('id, username, avatar, created_at, updated_at, role, banned, age_band')
      .eq('id', user.id)
      .maybeSingle(),
    client.from('game_saves').select('*').eq('user_id', user.id).maybeSingle(),
    client.from('dev_inbox').select('*').eq('recipient', user.id),
    // Which gift codes this account redeemed (spec 2026-10-09) — the codes'
    // own table is admin-only, so this is the redemption log alone.
    client.from('code_redemptions').select('code, redeemed_at').eq('user_id', user.id),
    // When this account last sent feedback, and the staff commands logged
    // against its name (spec 2026-10-24) — the feedback itself is never kept.
    client.rpc('export_my_feedback_and_commands'),
  ]);

  return {
    ok: true,
    data: {
      exportedAt: new Date().toISOString(),
      account: {
        id: user.id,
        // A synthetic address is an internal identifier rather than a real
        // mailbox; it is included because it is genuinely what we hold.
        email: user.email ?? null,
        createdAt: user.created_at,
        lastSignInAt: user.last_sign_in_at ?? null,
      },
      profile: profile.data ?? null,
      gameSave: save.data ?? null,
      messagesFromStaff: inbox.data ?? [],
      giftCodesRedeemed: codes.data ?? [],
      feedbackAndStaffCommands: extras.data ?? null,
      note:
        'This is everything Hambi Tower Defense holds about your account. ' +
        'Passwords and recovery codes are stored only as one-way hashes and ' +
        'cannot be included, or read by anyone.',
    },
  };
}

/** Offers the export as a file the player keeps. */
export function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Deletes the caller's account and everything attached to it.
 *
 * The RPC is keyed on auth.uid() alone, so it can never be aimed at another
 * account. It cascades to the profile, the cloud save and staff messages, and
 * removes the copy of the signup save held in the auth user's metadata.
 */
export async function deleteMyAccount(
  client: SupabaseClient,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { error } = await client.rpc('delete_own_account');
  if (error) return { ok: false, error: error.message };
  // The session now points at a user that no longer exists.
  await client.auth.signOut();
  return { ok: true };
}
